import React, { useEffect, useState, Dispatch, SetStateAction } from "react";
import { reduxForm, initialize, InjectedFormProps, isValid } from "redux-form";
import { ACTION_DISPLAY_TYPES, FIELD_TYPES } from "../../constants/enums";
import { useDispatch, useSelector } from "react-redux";
import { GetPaymentDisbursementHistoryResponse } from "../../types/api/user";
import { PaymentDisbursementHistorySearchParams } from "../../types/paymentDisbursementHistory";
import { FORM_NAMES, PAYMENT_DISBURSEMENT_HISTORY_FORM } from "../../constants/forms";
import { PaymentDisbursementHistoryFormValues } from "../../types/forms";
import UserApi from "../../api/user";
import { RootState } from "../../types/redux";
import useErrorModal from "../../hooks/useErrorModal";

import DynamicInputField from "../Common/DynamicFields/DynamicInputField";
import Action from "../Common/Action";

type PaymentDisbursementHistoryProps = {
  setResults: Dispatch<SetStateAction<GetPaymentDisbursementHistoryResponse | null>>;
  searchParams: PaymentDisbursementHistorySearchParams;
  setSearchParams: Dispatch<SetStateAction<PaymentDisbursementHistorySearchParams>>;
  setSubmitting: Dispatch<SetStateAction<boolean>>;
}

const SearchForm: React.FC<PaymentDisbursementHistoryProps & InjectedFormProps<PaymentDisbursementHistoryFormValues, PaymentDisbursementHistoryProps>> = ({ handleSubmit, reset, submitting, setResults, searchParams, setSearchParams, setSubmitting }) => {
  const dispatch = useDispatch();
  const [resetDateFields, setResetDateFields] = useState(false);
  const valid = useSelector((state: RootState) => isValid(FORM_NAMES.PAYMENT_DISBURSEMENT_HISTORY)(state));
  const openErrorModal = useErrorModal();

  /**
   * Initialize form with default values on initial render
   */
  useEffect(() => {
    dispatch(initialize(FORM_NAMES.PAYMENT_DISBURSEMENT_HISTORY, {
      showDisbursements: "false",
      beneficiaryNameFilterBy: "startswith",
      pageSize: "10"
    }));
  }, []);

  /**
   * If form is valid and submitSearch is true, submit the form
   * This is triggered by the ResultsTable component when a column sort button is clicked
   * We want to explicitly check for submitSearch because right when we clear values in the form, the form is still valid but we don't want to submit the form
   */
  useEffect(() => {
    if (valid && searchParams.submitSearch) {
      handleSubmit((values: PaymentDisbursementHistoryFormValues) => onSubmit(values, false))();
    }
  }, [searchParams.submitSearch]);

  /**
   * Update submitting state to parent component so that ResultsTable component can display a loading skeleton
   */
  useEffect(() => {
    setSubmitting(submitting);
  }, [submitting]);

  /**
   * Will reset the DateField hasReset state value after a delay
   * So that if user clicks clear multiple times, we can clear the DateField on each click
   */
  useEffect(() => {
    if (resetDateFields) {
      const timeout = setTimeout(() => {
        setResetDateFields(false);  // Stop resetting after the initial reset
      }, 100); // Adjust delay as needed

      return () => clearTimeout(timeout); // Clean up the timeout
    }
  }, [resetDateFields]);

  /**
   * Submit the form and set the results to parent component
   * Also update the searchParams so submitSearch is false
   * @param values Form values
   * @param isSearchClick If search button is clicked, set the page to 1, so the user gets brand new search
   */
  const onSubmit = async (values: PaymentDisbursementHistoryFormValues, isSearchClick: boolean) => {
    try {
      const requestBody = {
        ...values,
        page: isSearchClick ? 1 : searchParams.page,
        sortColumn: isSearchClick ? "DateTime" : searchParams.sortColumn,
        sortOrder: isSearchClick ? 1 : searchParams.sortOrder,
        beneficiaryNameFilterBy: values.beneficiaryName ? values.beneficiaryNameFilterBy : ""
      }
      const response = await UserApi.GetPaymentDisbursementHistory(requestBody) as GetPaymentDisbursementHistoryResponse;
      setResults(response);
      setSearchParams({
        page: isSearchClick ? 1 : searchParams.page,
        sortColumn: isSearchClick ? "DateTime" : searchParams.sortColumn,
        sortOrder: isSearchClick ? 1 : searchParams.sortOrder,
        submitSearch: false
      });
    } catch (err) {
      openErrorModal(err);
    }
  }

  const handleReset = () => {
    setResults(null);
    setResetDateFields(true);
    setSearchParams({
      ...searchParams,
      page: 1,
      sortOrder: 1,
      sortColumn: "DateTime",
    })
    reset();
  }

  return (
    <form onSubmit={handleSubmit((values: PaymentDisbursementHistoryFormValues) => onSubmit(values, true))}>
      <section className="grid grid-cols-2">
        <DynamicInputField
          id={PAYMENT_DISBURSEMENT_HISTORY_FORM.START_DATE.FIELD_NAME}
          name={PAYMENT_DISBURSEMENT_HISTORY_FORM.START_DATE.FIELD_NAME}
          label={PAYMENT_DISBURSEMENT_HISTORY_FORM.START_DATE.LABEL}
          type={FIELD_TYPES.DATE}
          resetDateFields={resetDateFields}
          required
        />

        <DynamicInputField
          id={PAYMENT_DISBURSEMENT_HISTORY_FORM.END_DATE.FIELD_NAME}
          name={PAYMENT_DISBURSEMENT_HISTORY_FORM.END_DATE.FIELD_NAME}
          label={PAYMENT_DISBURSEMENT_HISTORY_FORM.END_DATE.LABEL}
          type={FIELD_TYPES.DATE}
          resetDateFields={resetDateFields}
          setResetDateFields={() => setResetDateFields(false)}
          required
        />

        <DynamicInputField
          id={PAYMENT_DISBURSEMENT_HISTORY_FORM.BENEFICIARY_NAME.FIELD_NAME}
          name={PAYMENT_DISBURSEMENT_HISTORY_FORM.BENEFICIARY_NAME.FIELD_NAME}
          label={PAYMENT_DISBURSEMENT_HISTORY_FORM.BENEFICIARY_NAME.LABEL}
          multipleInputNames={{
            select: PAYMENT_DISBURSEMENT_HISTORY_FORM.BENEFICIARY_NAME.SELECT_FIELD_NAME,
            text: PAYMENT_DISBURSEMENT_HISTORY_FORM.BENEFICIARY_NAME.FIELD_NAME,
          }}
          options={[
            { value: "startswith", label: "Starts With" },
            { value: "contains", label: "Contains" },
            { value: "endswith", label: "Ends With" },
          ]}
          type={FIELD_TYPES.SELECT_TEXT_COMBO}
        />

        <DynamicInputField
          id={PAYMENT_DISBURSEMENT_HISTORY_FORM.SHOW_DISBURSEMENTS.FIELD_NAME}
          label={PAYMENT_DISBURSEMENT_HISTORY_FORM.SHOW_DISBURSEMENTS.LABEL}
          type={FIELD_TYPES.SELECT}
          options={[
            { value: "false", label: "No" },
            { value: "true", label: "Yes" }
          ]}
          required={false}
        />

        <DynamicInputField
          id={PAYMENT_DISBURSEMENT_HISTORY_FORM.PAGE_SIZE.FIELD_NAME}
          label={PAYMENT_DISBURSEMENT_HISTORY_FORM.PAGE_SIZE.LABEL}
          type={FIELD_TYPES.SELECT}
          options={[
            { value: "10", label: "10" },
            { value: "25", label: "25" },
            { value: "50", label: "50" }
          ]}
          required={false}
          className="form-group max-w-250"
        />
      </section>

      <div className="flex-row justify-content-start mobile-direction-column gap-16">
        <Action
          title={"Clear"}
          displayType={ACTION_DISPLAY_TYPES.SECONDARY}
          onClick={handleReset}
          disabled={false}
          loading={false}
          type="button"
        />

        <Action
          title={"Search"}
          displayType={ACTION_DISPLAY_TYPES.PRIMARY}
          onClick={() => handleSubmit((values: PaymentDisbursementHistoryFormValues) => onSubmit(values, true))()}
          disabled={!valid}
          loading={submitting}
          type="submit"
        />
      </div>
    </form>
  );
}

export default reduxForm<PaymentDisbursementHistoryFormValues, PaymentDisbursementHistoryProps>({
  form: FORM_NAMES.PAYMENT_DISBURSEMENT_HISTORY,
  destroyOnUnmount: false,
})(SearchForm);